import type { SendEventParams } from '@/utils/sendEvent';
import { PixelResponse } from '../../types';
import { BaseTracker } from './BaseTracker';
import { getCoreVisitorId } from '@/core/tracker';

export interface IInitiateCheckoutData {
  value?: number;
  currency?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    category?: string;
    quantity: number;
    price: number;
  }>;
}

/**
 * Tracker para eventos de início de checkout
 */
export class InitiateCheckoutTracker extends BaseTracker {
  /**
   * Retorna o payload para envio centralizado no ConversionEngine.
   */
  public getPayload({
    value = 0,
    currency = 'BRL',
    items,
  }: IInitiateCheckoutData): SendEventParams {
    return {
      category: 'ECOMMERCE',
      type: 'INITIATE_CHECKOUT',
      eventData: {
        value,
        currency,
        items,
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    };
  }

  /**
   * Rastreia início de checkout
   */
  public async track({
    value = 0,
    currency = 'BRL',
    items,
  }: IInitiateCheckoutData): Promise<PixelResponse> {
    return this.sendEvent(this.getPayload({ value, currency, items }));
  }

  /**
   * Rastreia início de checkout com cálculo automático do valor
   */
  public async trackWithCalculatedValue(
    items: Array<{
      item_id: string;
      item_name: string;
      category?: string;
      quantity: number;
      price: number;
    }>,
    currency: string = 'BRL'
  ): Promise<PixelResponse> {
    if (!items || items.length === 0) {
      throw new Error('Lista de itens não pode estar vazia');
    }

    const totalValue = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    return this.sendEvent({
      category: 'ECOMMERCE',
      type: 'INITIATE_CHECKOUT',
      eventData: {
        value: totalValue,
        currency,
        items,
        total_items: items.length,
        calculated_value: true,
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    });
  }

  /**
   * Rastreia início de checkout com parâmetros customizados
   */
  public async trackWithCustomParams(
    checkoutData: IInitiateCheckoutData,
    customParams: Record<string, unknown>
  ): Promise<PixelResponse> {
    return this.sendEvent({
      category: 'ECOMMERCE',
      type: 'INITIATE_CHECKOUT',
      eventData: {
        value: checkoutData.value || 0,
        currency: checkoutData.currency || 'BRL',
        items: checkoutData.items ?? [],
        total_items: checkoutData.items?.length || 0,
        ...customParams,
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    });
  }
}

// Função de conveniência para compatibilidade com código existente
export const initiateCheckout = ({
  value = 0,
  currency = 'BRL',
  items,
}: IInitiateCheckoutData): Omit<
  SendEventParams,
  'config' | 'sessionId' | 'version'
> => {
  const visitorId = getCoreVisitorId();
  if (!visitorId) {
    throw new Error('Visitor ID não encontrado');
  }
  return {
    visitorId: visitorId,
    category: 'ECOMMERCE',
    type: 'INITIATE_CHECKOUT',
    eventData: {
      value,
      currency,
      items: items ?? [],
    },
  };
};
